import { useEffect, useRef } from 'react';

export const WAVE_BARS = 40;
/** Bar height range, px: the row is 110px tall (see `.voice-wave`). */
const MIN_H = 4;
const MAX_RISE = 102;

export type WavePhase = 'idle' | 'connecting' | 'live' | 'ended';
export type Speaker = 'ai' | 'you' | null;

/**
 * Loudness of one analyser frame, 0..1. Speech RMS sits around 0.03–0.25, so
 * it is scaled up and square-rooted to give quiet syllables a visible lift.
 */
export function levelOf(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const s of samples) sum += s * s;
  const rms = Math.sqrt(sum / samples.length);
  return Math.min(1, Math.sqrt(rms * 4));
}

/** Rise with the voice at once, fall away gently between words. */
export function smoothLevel(prev: number, next: number): number {
  return prev + (next - prev) * (next > prev ? 0.6 : 0.12);
}

/** Level the voice must pass to count as speaking, and fall below to stop. */
const SPEAK_ON = 0.18;
const SPEAK_OFF = 0.07;

/** Whether a voice counts as speaking — with hysteresis, so pauses between
 * syllables do not flip the status back and forth. */
export function isSpeaking(was: boolean, level: number): boolean {
  return was ? level > SPEAK_OFF : level > SPEAK_ON;
}

/** The assistant wins when both are heard (the caller's mic hears its echo);
 * the caller's voice is drawn smaller, as in the design. */
export function waveTarget(phase: WavePhase, who: Speaker, ai: number, you: number, t: number): number {
  if (phase === 'connecting') return 0.08 + 0.06 * Math.sin(t * 6);
  if (phase !== 'live') return 0;
  return who === 'ai' ? ai : who === 'you' ? 0.55 * you : 0;
}

/**
 * Height in px of bar `i` of `n` at time `t` (s) for amplitude `amp` 0..1. A
 * bell keeps the middle tallest; a fast flicker keeps it from reading as one
 * shape. At rest (`idle`) a slow, low ripple runs along the row.
 */
export function barHeight(i: number, n: number, t: number, amp: number, idle: boolean): number {
  const c = (n - 1) / 2;
  const env = 0.3 + 0.7 * Math.exp(-(((i - c) / (n * 0.26)) ** 2));
  if (idle) return MIN_H + 3 * (0.5 + 0.5 * Math.sin(t * 1.6 - i * 0.35)) * env;
  const flicker = 0.55 + 0.45 * Math.abs(Math.sin(t * 8.7 + i * 1.9) * Math.cos(t * 4.1 + i * 0.7));
  return MIN_H + MAX_RISE * amp * env * flicker;
}

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/** An analyser on a stream, never connected to the speakers. */
function analyse(context: AudioContext | null, stream: MediaStream | null) {
  if (!context || !stream || stream.getAudioTracks().length === 0) return null;
  try {
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    return {
      level: () => {
        analyser.getFloatTimeDomainData(samples);
        return levelOf(samples);
      },
      close: () => source.disconnect(),
    };
  } catch {
    return null;
  }
}

/**
 * The Try step's voice wave (design: Onboarding · Try it). Bars rise with the
 * assistant's voice (the remote WebRTC stream — the <audio> element plays it)
 * in the brand colour with a halo behind them, and with the caller's own voice
 * (their mic) smaller and muted; at rest a faint ripple. Everything per frame
 * goes through refs, so React renders the row once; only a change of speaker
 * reaches React, through `onSpeaker`.
 */
export function CallWave({
  phase,
  remote,
  mic,
  context,
  onSpeaker,
}: {
  phase: WavePhase;
  remote: MediaStream | null;
  mic: MediaStream | null;
  /** Made in the Start click (Safari); the caller closes it. */
  context: AudioContext | null;
  onSpeaker?: (who: Speaker) => void;
}) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const haloRef = useRef<HTMLDivElement | null>(null);
  const barsRef = useRef<(HTMLSpanElement | null)[]>([]);
  const heights = useRef<number[]>(new Array(WAVE_BARS).fill(MIN_H));
  const amp = useRef(0);
  const onSpeakerRef = useRef(onSpeaker);
  onSpeakerRef.current = onSpeaker;

  useEffect(() => {
    void context?.resume().catch(() => undefined);
    const ai = phase === 'live' ? analyse(context, remote) : null;
    const you = phase === 'live' ? analyse(context, mic) : null;
    const still = reducedMotion();
    const idle = phase === 'idle' || phase === 'ended';
    const row = rowRef.current;
    let aiLevel = 0;
    let youLevel = 0;
    let aiOn = false;
    let youOn = false;
    let who: Speaker = null;
    let frame = 0;

    const tick = (now: number) => {
      const t = still ? 0 : now / 1000;
      if (ai) aiLevel = smoothLevel(aiLevel, ai.level());
      if (you) youLevel = smoothLevel(youLevel, you.level());
      aiOn = isSpeaking(aiOn, aiLevel);
      youOn = isSpeaking(youOn, youLevel);
      const nextWho: Speaker = aiOn ? 'ai' : youOn ? 'you' : null;
      if (nextWho !== who) {
        who = nextWho;
        if (row) row.dataset.who = who ?? '';
        onSpeakerRef.current?.(who);
      }

      amp.current += (waveTarget(phase, who, aiLevel, youLevel, t) - amp.current) * 0.25;
      heights.current.forEach((h, i) => {
        const next = h + (barHeight(i, WAVE_BARS, t, amp.current, idle) - h) * 0.35;
        heights.current[i] = next;
        const bar = barsRef.current[i];
        if (bar) bar.style.height = `${next.toFixed(1)}px`;
      });
      const halo = haloRef.current;
      if (halo) {
        halo.style.opacity = (who === 'ai' ? Math.min(1, amp.current * 1.6) : 0).toFixed(2);
        halo.style.transform = `scale(${(0.85 + amp.current * 0.35).toFixed(3)})`;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      ai?.close();
      you?.close();
      if (who !== null) onSpeakerRef.current?.(null);
      if (row) row.dataset.who = '';
    };
  }, [phase, remote, mic, context]);

  return (
    <div className={`voice-stage is-${phase}`} aria-hidden="true">
      <div ref={haloRef} className="voice-stage__halo" />
      <div ref={rowRef} className="voice-wave">
        {Array.from({ length: WAVE_BARS }, (_, i) => (
          <span
            key={i}
            ref={(el) => {
              barsRef.current[i] = el;
            }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * An audio context for {@link CallWave}, made inside the click that starts the
 * call: Safari keeps one created later suspended, and the wave would then
 * never hear the voices. Null where Web Audio is missing.
 */
export function createWaveAudioContext(): AudioContext | null {
  try {
    return typeof AudioContext === 'undefined' ? null : new AudioContext();
  } catch {
    return null;
  }
}
